import * as React from "react";
import { NavLink, useLocation } from "react-router";
import {
  Activity,
  BadgeDollarSign,
  Building2,
  Cpu,
  FileSearch,
  Inbox,
  LibraryBig,
  ListVideo,
  Menu,
  Scale,
  ScrollText,
  Webhook,
} from "lucide-react";
import { Button, Sheet, Wordmark, cn } from "@moanhot/ui";
import { NAV, type NavKey } from "@/nav";

const ICONS: Record<NavKey, React.ElementType> = {
  applications: Inbox,
  tenants: Building2,
  prices: BadgeDollarSign,
  catalogue: LibraryBig,
  generations: ListVideo,
  screening: FileSearch,
  webhooks: Webhook,
  reconciliation: Scale,
  runpod: Cpu,
  audit: ScrollText,
};

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav aria-label="Admin" className="flex-1 overflow-y-auto px-3 pt-3">
      <ul className="space-y-px">
        {NAV.map((n) => {
          const Icon = ICONS[n.key];
          return (
            <li key={n.key}>
              <NavLink
                to={n.path}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "flex h-8 w-full items-center gap-2.5 rounded-sm px-2 text-sm transition-colors duration-150",
                    isActive ? "bg-signal-soft font-medium text-signal-text" : "text-ink-2 hover:bg-hover hover:text-ink",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={cn("size-4", isActive ? "text-signal-text" : "text-ink-3")} aria-hidden />
                    {n.label}
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = React.useState(false);
  const title = NAV.find((n) => pathname.startsWith(n.path))?.label ?? "Admin";
  return (
    <div className="flex min-h-screen bg-canvas">
      <a href="#admin-main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-sm focus:bg-raised focus:px-3 focus:py-2">
        Skip to content
      </a>
      <aside className="sticky top-0 hidden h-screen w-[232px] shrink-0 flex-col border-r border-line bg-surface md:flex">
        <div className="flex h-12 items-center border-b border-line px-5">
          <Wordmark suffix="admin" />
        </div>
        <SidebarNav />
        <div className="border-t border-line px-5 py-3 text-xs text-ink-3">
          <Activity className="mr-1.5 inline size-3.5 align-[-2px]" aria-hidden />
          Operator console
        </div>
      </aside>
      <Sheet open={navOpen} onOpenChange={setNavOpen} side="left" title={<Wordmark suffix="admin" />} className="bg-surface">
        <SidebarNav onNavigate={() => setNavOpen(false)} />
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-12 items-center gap-2 border-b border-line bg-canvas px-3 md:px-6">
          <Button variant="ghost" size="icon" className="-ml-1 md:hidden" aria-label="Open navigation" onClick={() => setNavOpen(true)}>
            <Menu />
          </Button>
          <div className="min-w-0 flex-1 truncate text-sm text-ink">{title}</div>
        </header>
        <main id="admin-main" className="min-w-0 flex-1">
          {children}
        </main>
      </div>
    </div>
  );
}
